import React, { useRef, useState } from 'react';
import { Dialog, DialogActions, DialogContent, DialogTitle, Button, Box, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import ReactCrop, { centerCrop, makeAspectCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';

const ASPECT_OPTIONS = [
  { label: 'Free', value: null },
  { label: '1:1', value: 1 },
  { label: '4:3', value: 4 / 3 },
  { label: '16:9', value: 16 / 9 },
  { label: '3:1 (Banner)', value: 3 },
];

function centerAspectCrop(mediaWidth, mediaHeight, aspect) {
  return centerCrop(
    makeAspectCrop(
      { unit: 'px', width: mediaWidth * 0.9 },
      aspect || mediaWidth / mediaHeight,
      mediaWidth,
      mediaHeight,
    ),
    mediaWidth,
    mediaHeight,
  );
}

async function getCroppedBlob(image, crop, fileType) {
  const canvas = document.createElement('canvas');
  const scaleX = image.naturalWidth / image.width;
  const scaleY = image.naturalHeight / image.height;
  const pixelWidth = crop.width * scaleX;
  const pixelHeight = crop.height * scaleY;

  canvas.width = pixelWidth;
  canvas.height = pixelHeight;
  const ctx = canvas.getContext('2d');

  ctx.drawImage(
    image,
    crop.x * scaleX,
    crop.y * scaleY,
    pixelWidth,
    pixelHeight,
    0,
    0,
    pixelWidth,
    pixelHeight,
  );

  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), fileType || 'image/jpeg', 0.92);
  });
}

function ImageCropDialog({ open, file, onCancel, onConfirm, title, confirmLabel, recommendedAspect }) {
  const imgRef = useRef(null);
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState();
  const [aspect, setAspect] = useState(recommendedAspect ? recommendedAspect.value : null);

  const aspectOptions = recommendedAspect
    ? [recommendedAspect, ...ASPECT_OPTIONS.filter((opt) => opt.value !== recommendedAspect.value)]
    : ASPECT_OPTIONS;

  React.useEffect(() => {
    if (file) {
      setAspect(recommendedAspect ? recommendedAspect.value : null);
      const reader = new FileReader();
      reader.onload = () => setImageSrc(reader.result);
      reader.readAsDataURL(file);
    } else {
      setImageSrc(null);
      setCrop(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file]);

  const onImageLoad = (e) => {
    const { width, height } = e.currentTarget;
    setCrop(centerAspectCrop(width, height, aspect));
  };

  const aspectChange = (event, value) => {
    setAspect(value);
    if (imgRef.current) {
      const { width, height } = imgRef.current;
      setCrop(centerAspectCrop(width, height, value));
    }
  };

  const handleConfirm = async () => {
    if (!imgRef.current || !crop || !crop.width || !crop.height) {
      onConfirm(file);
      return;
    }
    const blob = await getCroppedBlob(imgRef.current, crop, file?.type);
    const croppedFile = new File([blob], file?.name || 'cropped-image.jpg', { type: file?.type || 'image/jpeg' });
    onConfirm(croppedFile);
  };

  const handleUseOriginal = () => {
    onConfirm(file);
  };

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="md" fullWidth>
      <DialogTitle>{title || 'Crop Image'}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
          {recommendedAspect
            ? `Recommended aspect ratio for this slot: ${recommendedAspect.label}. Adjust the crop area, pick a different ratio, or use the original.`
            : 'Adjust the crop area to fit the image neatly, or pick an aspect ratio. You can also skip cropping and use the original.'}
        </Typography>
        <ToggleButtonGroup
          value={aspect}
          exclusive
          onChange={aspectChange}
          size="small"
          sx={{ mb: 2, flexWrap: 'wrap' }}
        >
          {aspectOptions.map((opt) => (
            <ToggleButton key={opt.label} value={opt.value}>{opt.label}</ToggleButton>
          ))}
        </ToggleButtonGroup>
        {imageSrc && (
          <Box sx={{ display: 'flex', justifyContent: 'center', background: '#f4f5fa', padding: 1 }}>
            <ReactCrop
              crop={crop}
              onChange={(pixelCrop) => setCrop(pixelCrop)}
              aspect={aspect || undefined}
            >
              <img
                ref={imgRef}
                src={imageSrc}
                alt="To crop"
                onLoad={onImageLoad}
                style={{ maxHeight: '60vh' }}
              />
            </ReactCrop>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button onClick={handleUseOriginal}>Use Original</Button>
        <Button variant="contained" onClick={handleConfirm}>{confirmLabel || 'Crop & Insert'}</Button>
      </DialogActions>
    </Dialog>
  );
}

export default ImageCropDialog;
